import React, { useMemo, useState } from 'react'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import LoadingSpinner from '../../components/loading/loading_spinner'
import AiLoadingOverlay from '../../components/loading/ai_loading_overlay'
import { useOwnRecommendation } from '../../hooks/use_own_recommendation'
import BusinessIdeaCard from '../../components/cards/business_idea_card'
import RadioButtonGroup from '../../components/buttons/radio_button_group'
import { useInfiniteScroll } from '../../hooks/use_infinite_scroll'
import type { RadioButtonType } from '../../components/buttons/radio_button_group'
import FlatButton from '../../components/buttons/flat_button'
import BusinessIdeaGenerateModal from '../../components/modals/business_idea_generate_modal'
import { useBusinessIdeaGenerate } from '../../hooks/use_business_idea_generate'
import { useBusinessDelete } from '../business_detail/hooks/use_business_delete'
import DeleteConfirmModal from '../../components/modals/delete_confirm_modal'
import {
  RecommendationSortType,
  RecommendationSortTypeLabel,
} from '../../types/recommendation'
import { useCheckGenerateLimit } from './hooks/use_check_generate_limit'
import DashboardLink from '../../components/layouts/side_menu/dashboard_link'
import { TopMessageSection } from '../../components/sections/top_message_section'
import Breadcrumb from '../../components/common/breadcrumb'

const MyIdeaPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const {
    isLoading: isCheckingGenerateLimit,
    enableGenerate,
    isPaidPlan,
    remainCount,
  } = useCheckGenerateLimit()
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  const {
    isLoading,
    isLoadingMore, // 追加ローディング中かどうか
    hasMore, // 追加読み込み可能かどうか
    recommendations,
    loadMore,
    changeSort,
    load,
  } = useOwnRecommendation(9, RecommendationSortType.CREATED_AT)

  const { isGenerating, generateBusinessIdea } = useBusinessIdeaGenerate()
  const { isDeleting, deleteBusinessIdea } = useBusinessDelete()

  // 無限スクロールの設定
  const { LoadingElement } = useInfiniteScroll(loadMore, isLoadingMore, hasMore)

  const sortButtons: RadioButtonType[] = useMemo(
    () => [
      {
        label: RecommendationSortTypeLabel[RecommendationSortType.CREATED_AT],
        onClick: () => changeSort(RecommendationSortType.CREATED_AT),
      },
      {
        label: RecommendationSortTypeLabel[RecommendationSortType.EVALUATION],
        onClick: () => changeSort(RecommendationSortType.EVALUATION),
      },
      {
        label: RecommendationSortTypeLabel[RecommendationSortType.NAME],
        onClick: () => changeSort(RecommendationSortType.NAME),
      },
    ],
    [changeSort]
  )

  const handleGenerate = (aiInstruction: string | null) => {
    generateBusinessIdea(aiInstruction, () => {
      setIsModalOpen(false)
      load(false)
    })
  }

  const handleDelete = (businessIdeaId: string, projectName: string) => {
    setDeleteTarget({ id: businessIdeaId, name: projectName })
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return

    const success = await deleteBusinessIdea(deleteTarget.id)
    if (success) {
      setIsDeleteModalOpen(false)
      setDeleteTarget(null)
      // 削除成功後、リストを再読み込み
      load(false)
    }
  }

  const handleDeleteModalClose = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false)
      setDeleteTarget(null)
    }
  }

  return (
    <AuthenticatedLayout>
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { label: 'ホーム', path: '/dashboard' },
            { label: 'マイアイデア' },
          ]}
        />

        {!isCheckingGenerateLimit && !isPaidPlan && (
          <TopMessageSection>
            <div className="mb-2">
              <p>従量課金プランの場合は自動アイデア生成数に制限があります。</p>
              <p>有料プランに切り替えた場合は制限が解除されます。</p>
            </div>
            <p className="mb-4">
              残り生成可能数:
              <span className="text-main-green font-bold pl-2">
                {remainCount}回
              </span>
            </p>
            <DashboardLink>
              <FlatButton>有料プランに切り替える</FlatButton>
            </DashboardLink>
          </TopMessageSection>
        )}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-10">
            <h1 className="text-2xl font-bold">マイアイデア</h1>
            <FlatButton
              onClick={() => setIsModalOpen(true)}
              disabled={!enableGenerate}
            >
              自動生成する
            </FlatButton>
          </div>
          <RadioButtonGroup buttons={sortButtons} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {isLoading && recommendations.length === 0 ? (
            <div className="flex justify-center py-20 col-span-full">
              <LoadingSpinner />
            </div>
          ) : recommendations.length === 0 ? (
            <div className="col-span-full">
              <div className="text-center py-8 text-main-darkgray">
                アイデアがありません。
              </div>
            </div>
          ) : (
            <>
              {recommendations.map((recommendation, index) => (
                <BusinessIdeaCard
                  key={`${recommendation.businessIdeaId}-${index}`}
                  recommendation={recommendation}
                  initialized={!isLoading}
                  onDelete={handleDelete}
                />
              ))}
            </>
          )}

          {recommendations.length > 0 && hasMore && LoadingElement}
        </div>

        <BusinessIdeaGenerateModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onGenerate={handleGenerate}
          isGenerating={isGenerating}
        />

        <AiLoadingOverlay
          message="AI生成中です..."
          description="生成には約2分程度かかります"
          isVisible={isGenerating}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={handleDeleteModalClose}
          onConfirm={handleDeleteConfirm}
          itemName={deleteTarget?.name || '事業アイデア'}
          isDeleting={isDeleting}
        />
      </div>
    </AuthenticatedLayout>
  )
}

export default MyIdeaPage
